'use client'

import { useQueries } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { useEffect, useMemo, useState } from 'react'

import { sumAccountEquities } from '@/features/dashboard/lib/account-equity-aggregate'
import {
  aggregateAccountSummaries,
  aggregateHeadlinePnl,
} from '@/features/dashboard/lib/account-summary-aggregate'
import {
  parseDecimal,
  type PnlTimeframe,
  resolvePnlFallback,
} from '@/features/dashboard/lib/account-summary-display'
import {
  sumPortfolioSeries,
  windowPortfolioSeriesByTick,
} from '@/features/dashboard/lib/portfolio-chart-aggregate'
import { useUserAccounts } from '@/service/account.hook'
import { portfolioLineChartListQueryOptions } from '@/service/portfolio-line-chart.hook'
import { accountSummaryByIdAtom, totalBalanceUsdRawAtom } from '@/state/account'
import { AccountStatus } from '@/types/generated/common/account'
import type { TradeVenue } from '@/types/generated/common/types'

/** How long one silent account may hold the total back (Account Cards uses the same 15s). */
const EQUITY_PENDING_GRACE_MS = 15_000
const DAY_SECONDS = 86_400
const YEAR_SECONDS = 365 * DAY_SECONDS

// Same windows as the Account Cards widget (account-cards.tsx): the backend only keeps 300s and
// 3600s bars, so 1W/1M/ALL share the hourly series and are narrowed here.
const TIMEFRAME_WINDOW: Record<PnlTimeframe, { bar: number; lookbackSeconds: number | null }> = {
  '1D': { bar: 300, lookbackSeconds: DAY_SECONDS },
  '1W': { bar: 3600, lookbackSeconds: 7 * DAY_SECONDS },
  '1M': { bar: 3600, lookbackSeconds: 30 * DAY_SECONDS },
  ALL: { bar: 3600, lookbackSeconds: null },
}

export interface HomePortfolio {
  isAccountsLoading: boolean
  accountCount: number
  venues: TradeVenue[]
  /** Summed live equity in USD; null until every account has reported (or none is connected). */
  equityUsd: number | null
  /** Accounts in `equityUsd` vs. accounts connected — differ while one is still loading. */
  resolvedCount: number
  pnlUsd: number
  /** `pnlUsd` against the value at the start of the window, percent. */
  pnlPercent: number | null
  unrealizedPnlUsd: number | null
  netLeverage: number | null
  /** The summed equity curve over the window, oldest first, ending on the live value. */
  series: number[]
  isSeriesPending: boolean
}

/**
 * The whole portfolio in one place for Home: every active account's live equity (the SUMMARY
 * stream, PORTFOLIO as fallback), headline PnL for the chosen window resolved per account and
 * then summed (as Account Cards does in All Accounts mode), and the summed equity curve.
 */
export function useHomePortfolio(timeframe: PnlTimeframe): HomePortfolio {
  const { data, isPending: isAccountsLoading } = useUserAccounts()
  const summaryById = useAtomValue(accountSummaryByIdAtom)
  const totalBalanceById = useAtomValue(totalBalanceUsdRawAtom)
  const [historyFrom] = useState(() => String(Math.floor(Date.now() / 1000) - YEAR_SECONDS))

  const accounts = useMemo(
    () =>
      (data?.accounts ?? []).filter(
        (account) => account.status === AccountStatus.ACCOUNT_STATUS_ACTIVE
      ),
    [data?.accounts]
  )
  const accountIds = useMemo(() => accounts.map((account) => account.accountId), [accounts])

  const venueGroups = useMemo(() => {
    const groups = new Map<TradeVenue, string[]>()
    for (const account of accounts) {
      groups.set(account.tradeVenue, [...(groups.get(account.tradeVenue) ?? []), account.accountId])
    }
    return groups
  }, [accounts])

  const range = TIMEFRAME_WINDOW[timeframe]
  const { charts, isPending: isSeriesPending } = useQueries({
    queries: [...venueGroups.entries()].map(([tradeVenue, ids]) => ({
      ...portfolioLineChartListQueryOptions({
        accountIds: ids,
        tradeVenue,
        timeframe: range.bar,
        from: range.bar === 3600 ? historyFrom : undefined,
      }),
      enabled: ids.length > 0,
    })),
    combine: (results) => ({
      charts: results.flatMap((result) => result.data ?? []),
      isPending: results.some((result) => result.isPending),
    }),
  })

  // An account that never reports equity (venue down, a reconnect) would hold the total on its
  // skeleton forever; after the grace it is left out and the total shows as partial instead.
  const [graceOver, setGraceOver] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setGraceOver(true), EQUITY_PENDING_GRACE_MS)
    return () => clearTimeout(timer)
  }, [])

  const equity = useMemo(() => {
    const lastChartValue = new Map<string, number>()
    for (const chart of charts) {
      const last = chart.values[chart.values.length - 1]
      if (Number.isFinite(last)) lastChartValue.set(chart.accountId, last)
    }
    const byId: Record<string, number | undefined> = {}
    const settled = new Set<string>()
    for (const id of accountIds) {
      // Live SUMMARY first, then the PORTFOLIO stream, then the last bar of its history.
      const live = parseDecimal(summaryById[id]?.equityUsd)
      byId[id] = live !== null ? live.toNumber() : (totalBalanceById[id] ?? lastChartValue.get(id))
      if (byId[id] === undefined && graceOver) settled.add(id)
    }
    return sumAccountEquities(accountIds, byId, settled)
  }, [accountIds, charts, graceOver, summaryById, totalBalanceById])

  const windowedSeries = useMemo(
    () => windowPortfolioSeriesByTick(sumPortfolioSeries(charts), range.lookbackSeconds).values,
    [charts, range.lookbackSeconds]
  )

  const pnlUsd = useMemo(() => {
    const deltaByAccount = new Map<string, number | null>()
    for (const chart of charts) {
      const values = windowPortfolioSeriesByTick(chart, range.lookbackSeconds).values
      deltaByAccount.set(
        chart.accountId,
        values.length >= 2 ? values[values.length - 1] - values[0] : null
      )
    }
    return aggregateHeadlinePnl(
      timeframe,
      accounts.map((account) => ({
        summary: summaryById[account.accountId],
        fallbackPnl: resolvePnlFallback(
          timeframe,
          deltaByAccount.get(account.accountId) ?? null,
          0
        ),
      }))
    )
  }, [accounts, charts, summaryById, timeframe, range.lookbackSeconds])

  const aggregate = useMemo(
    () => aggregateAccountSummaries(accounts.map((account) => summaryById[account.accountId])),
    [accounts, summaryById]
  )

  const equityUsd = equity?.total ?? null
  const series = useMemo(() => {
    if (equityUsd === null) return windowedSeries
    return windowedSeries.length ? [...windowedSeries, equityUsd] : []
  }, [windowedSeries, equityUsd])
  const startValue = equityUsd !== null ? equityUsd - pnlUsd : null

  return {
    isAccountsLoading,
    accountCount: accounts.length,
    venues: [...venueGroups.keys()],
    equityUsd,
    resolvedCount: equity?.resolvedCount ?? 0,
    pnlUsd,
    pnlPercent: startValue && startValue > 0 ? (pnlUsd / startValue) * 100 : null,
    unrealizedPnlUsd: aggregate ? Number(aggregate.unrealizedPnlUsd) : null,
    netLeverage: aggregate ? Number(aggregate.netLeverage) : null,
    series,
    isSeriesPending,
  }
}
